import { useState, useEffect, useMemo } from 'react';
import { vehicleService } from '../services/vehicleService';
import { asService } from '../services/asService';
import { customerService } from '../services/customerService'; 
import { partService } from '../services/partService'; 
import * as helpers from '../utils/analysisHelpers';
import { getLocalDateString, getWeekRange } from '../utils/dashboardUtils';

export const useAnalysisData = () => {
  const [loading, setLoading] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [asRecords, setAsRecords] = useState([]);
  const [masterModels, setMasterModels] = useState([]);
  const [customers, setCustomers] = useState([]); 
  const [parts, setParts] = useState([]); 

  const [subTab, setSubTab] = useState('sales'); 

  const todayStr = getLocalDateString();
  
  // 매출 분석 상태
  const [salesPeriod, setSalesPeriod] = useState('일일'); 
  const [targetDate, setTargetDate] = useState(todayStr);
  const [salesSearch, setSalesSearch] = useState(''); 

  // 부품 분석 상태
  const [usedPartsPeriod, setUsedPartsPeriod] = useState('일일'); 
  const [usedPartsDate, setUsedPartsDate] = useState(todayStr);
  const [selectedModel, setSelectedModel] = useState('전체');
  const [usedPartsModelFilter, setUsedPartsModelFilter] = useState('전체');
  const [usedPartsSearch, setUsedPartsSearch] = useState('');

  // AS/고객 필터 상태
  const [asFilterPeriod, setAsFilterPeriod] = useState('전체'); 
  const [asFilterDate, setAsFilterDate] = useState(todayStr.substring(0, 7)); 
  const [custModelFilter, setCustModelFilter] = useState('전체');
  const [custTypeFilter, setCustTypeFilter] = useState('전체'); 

  const [stats, setStats] = useState({
    totalVehicles: 0, totalCustomers: 0, totalAsAmount: 0, todayAsAmount: 0,
    asStatus: { receipt: 0, completed: 0, progressing: 0, pending: 0 },
    modelData: [], warrantyData: { expiringSoon: 0, expired: 0, active: 0 },
    asType: { free: 0, paid: 0 }, topSymptoms: []                         
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const [v, m, c, p] = await Promise.all([
        vehicleService.getVehicles(), vehicleService.getVehicleModels(),
        customerService.getCustomers(), partService.getParts() 
      ]);
      let a = [];
      if (asService && typeof asService.getRecords === 'function') a = await asService.getRecords();
      
      // 외부 열람 계정에게는 고객 이름을 가린 채로 담는다(화면·인쇄·엑셀 모두 적용된다).
      setVehicles(v || []);
      setMasterModels(m || []);
      setCustomers(c || []);
      setParts(p || []);
      setAsRecords(a || []); // 이름 마스킹은 서비스 계층에서 처리됨
    } catch (err) {
      console.error("데이터 종합 분석 오류 발생:", err);
    }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const filteredAsRecordsForStats = useMemo(() => {
    return asRecords.filter(a => {
      if (asFilterPeriod === '전체') return true;
      const date = a.settleDate || a.asDate || '';
      if (asFilterPeriod === '월간') return date.startsWith(asFilterDate.substring(0, 7)); 
      if (asFilterPeriod === '연간') return date.startsWith(asFilterDate.substring(0, 4)); 
      return true;
    });
  }, [asRecords, asFilterPeriod, asFilterDate]);

  useEffect(() => {
    if (vehicles.length || filteredAsRecordsForStats.length || masterModels.length || customers.length) {
      helpers.calculateStatistics(vehicles, filteredAsRecordsForStats, masterModels, customers, todayStr, setStats);
    }
  }, [vehicles, filteredAsRecordsForStats, masterModels, customers]);

  const allCompletedAs = useMemo(() => asRecords.filter(r => r.status === '완료'), [asRecords]);

  const allPartsRaw = useMemo(() => {
    const usedPartsMap = {};
    allCompletedAs.forEach(r => {
      if (r.parts && Array.isArray(r.parts)) r.parts.forEach(p => { usedPartsMap[p.taskName] = (usedPartsMap[p.taskName] || 0) + (Number(p.qty) || 0); });
    });
    return parts.map(p => {
      const currentStock = Number(p.stock) || 0; const usedQty = usedPartsMap[p.partName] || 0;
      return { ...p, initialStock: currentStock + usedQty, usedQty, currentStock, value: currentStock * (Number(p.purchasePrice) || 0) };
    });
  }, [parts, allCompletedAs]);

  const periodDetailList = useMemo(() => {
    return allCompletedAs.filter(r => {
      const d = r.settleDate || r.asDate;
      if (!d) return false;
      let matchPeriod = false;
      if (salesPeriod === '전체') matchPeriod = true;
      else if (salesPeriod === '일일') matchPeriod = d === targetDate;
      else if (salesPeriod === '주간') { const range = getWeekRange(targetDate); matchPeriod = d >= range.start && d <= range.end; } 
      else if (salesPeriod === '월간') matchPeriod = d.startsWith(targetDate.substring(0, 7));
      if (!matchPeriod) return false;

      const term = String(salesSearch).toLowerCase();
      return String(r.customerName || '').toLowerCase().includes(term) || String(r.carNumber || '').toLowerCase().includes(term) || String(r.mechanic || '').toLowerCase().includes(term);
    }).map(r => {
      let pSales = 0; let lSales = 0;
      if (r.parts) r.parts.forEach(p => { pSales += (Number(p.price) || 0) * (Number(p.qty) || 0); lSales += (Number(p.labor) || 0); });
      return { ...r, costExVat: Math.round(((Number(r.totalCost) || 0) + (Number(r.discount) || 0)) / 1.1), partsSales: pSales, laborSales: lSales };
    });
  }, [allCompletedAs, salesPeriod, targetDate, salesSearch]);

  const periodTotalSales = useMemo(() => periodDetailList.reduce((sum, r) => sum + r.costExVat, 0), [periodDetailList]);

  const monthlySalesTrend = useMemo(() => {
    const trend = []; const now = new Date(todayStr); 
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      let tSales = 0; let pSales = 0; let lSales = 0;
      allCompletedAs.filter(r => (r.settleDate || r.asDate || '').startsWith(monthKey)).forEach(r => {
        tSales += Math.round(((Number(r.totalCost) || 0) + (Number(r.discount) || 0)) / 1.1);
        if (r.parts) r.parts.forEach(p => { pSales += (Number(p.price) || 0) * (Number(p.qty) || 0); lSales += (Number(p.labor) || 0); });
      });
      trend.push({ month: monthKey, total: tSales, parts: pSales, labor: lSales });
    }
    return trend;
  }, [allCompletedAs, todayStr]);
  
  const filteredParts = useMemo(() => selectedModel === '전체' ? allPartsRaw : allPartsRaw.filter(p => (p.modelName || '공용부품') === selectedModel), [allPartsRaw, selectedModel]);
  const filteredTotalValue = filteredParts.reduce((acc, cur) => acc + cur.value, 0);

  const rawPeriodUsedPartsList = useMemo(() => {
    const result = [];
    allCompletedAs.forEach(r => {
      const d = r.settleDate || r.asDate;
      if (!d) return;
      let isMatch = false;
      if (usedPartsPeriod === '전체') isMatch = true;
      else if (usedPartsPeriod === '일일') isMatch = (d === usedPartsDate);
      else if (usedPartsPeriod === '주간') { const range = getWeekRange(usedPartsDate); isMatch = (d >= range.start && d <= range.end); }
      else if (usedPartsPeriod === '월간') isMatch = d.startsWith(usedPartsDate.substring(0, 7));
      else if (usedPartsPeriod === '하절기') {
        const rM = parseInt(d.split('-')[1], 10); isMatch = (d.split('-')[0] === usedPartsDate.substring(0, 4)) && (rM >= 5 && rM <= 8);
      } else if (usedPartsPeriod === '동절기') {
        const rM = parseInt(d.split('-')[1], 10); isMatch = (d.split('-')[0] === usedPartsDate.substring(0, 4)) && (rM === 11 || rM === 12 || (rM >= 1 && rM <= 3));
      }

      if (isMatch && r.parts) r.parts.forEach(p => {
        if (Number(p.qty) > 0) {
          const matchedPart = allPartsRaw.find(part => part.partName === (p.taskName || p.partName || '') || (p.partNo && part.partNo === p.partNo));
          if (matchedPart) result.push({ date: d, customerInfo: `${r.customerName} (${r.carNumber})`, mechanic: r.mechanic || '-', partName: matchedPart.partName, modelName: matchedPart.modelName || '공용부품', qty: Number(p.qty), unitCost: Number(matchedPart.purchasePrice) || 0, totalCost: (Number(matchedPart.purchasePrice) || 0) * Number(p.qty) });
        }
      });
    });
    return result;
  }, [allCompletedAs, usedPartsPeriod, usedPartsDate, allPartsRaw]);

  const filteredPeriodUsedPartsList = useMemo(() => rawPeriodUsedPartsList.filter(p => (usedPartsModelFilter === '전체' || p.modelName === usedPartsModelFilter) && (String(p.partName || '').toLowerCase().includes(String(usedPartsSearch).toLowerCase()) || String(p.customerInfo || '').toLowerCase().includes(String(usedPartsSearch).toLowerCase()))), [rawPeriodUsedPartsList, usedPartsModelFilter, usedPartsSearch]);

  const aggregatedPartsRanking = useMemo(() => {
    const map = {};
    filteredPeriodUsedPartsList.forEach(item => {
      if (!map[item.partName]) map[item.partName] = { partName: item.partName, modelName: item.modelName, totalQty: 0, totalCost: 0, currentStock: Number((parts.find(p => p.partName === item.partName) || {}).stock || 0) };
      map[item.partName].totalQty += item.qty; map[item.partName].totalCost += item.totalCost;
    });
    return Object.values(map).sort((a, b) => b.totalQty - a.totalQty);
  }, [filteredPeriodUsedPartsList, parts]);

  const periodTotalUsedQty = useMemo(() => filteredPeriodUsedPartsList.reduce((sum, item) => sum + item.qty, 0), [filteredPeriodUsedPartsList]);
  const periodTotalUsedCost = useMemo(() => filteredPeriodUsedPartsList.reduce((sum, item) => sum + item.totalCost, 0), [filteredPeriodUsedPartsList]);

  const partsValue = useMemo(() => {
    let totalV = 0; const modelVMap = {};
    parts.forEach(p => { const value = (Number(p.stock) || 0) * (Number(p.purchasePrice) || 0); totalV += value; modelVMap[p.modelName || '공용부품'] = (modelVMap[p.modelName || '공용부품'] || 0) + value; });
    return { total: totalV, dailyChange: 0, byModel: modelVMap };
  }, [parts]);

  const filteredCustomersList = useMemo(() => customers.filter(c => {
    const v = vehicles.find(item => item.carNumber === c.carNumber);
    return (custTypeFilter === '전체' || c.type === custTypeFilter) && (custModelFilter === '전체' || (v ? v.modelName : '미지정') === custModelFilter);
  }), [customers, custTypeFilter, custModelFilter, vehicles]);

  const getDisplayDate = () => {
    if (usedPartsPeriod === '전체') return '전체 누적 기간';
    if (usedPartsPeriod === '하절기') return `${usedPartsDate.substring(0,4)}년 하절기(5~8월)`;
    if (usedPartsPeriod === '동절기') return `${usedPartsDate.substring(0,4)}년 동절기(11~3월)`;
    if (usedPartsPeriod === '월간') return `${usedPartsDate.substring(0,7)}월`;
    return usedPartsDate;
  };
  const displayDate = getDisplayDate();

  return {
    loading, loadData, subTab, setSubTab,
    salesPeriod, setSalesPeriod, targetDate, setTargetDate, salesSearch, setSalesSearch,
    usedPartsPeriod, setUsedPartsPeriod, usedPartsDate, setUsedPartsDate, selectedModel, setSelectedModel,
    usedPartsModelFilter, setUsedPartsModelFilter, usedPartsSearch, setUsedPartsSearch,
    asFilterPeriod, setAsFilterPeriod, asFilterDate, setAsFilterDate,
    custModelFilter, setCustModelFilter, custTypeFilter, setCustTypeFilter,
    stats, vehicles, asRecords, customers, parts, masterModels,
    periodDetailList, periodTotalSales, monthlySalesTrend,
    periodTotalUsedQty, periodTotalUsedCost, aggregatedPartsRanking,
    filteredParts, filteredTotalValue, partsValue,
    filteredCustomersList, displayDate, todayStr
  };
};